import type { Address, Contact, Phone, ThirdPartyDriver } from './overlays';
import type { Statistics } from '../composables/statistics';
import type { RepairLocation as RepairLocationLight } from './repairFinder';
import type {
  ApiWidgetOptions,
  InsuranceConfig,
  StepKey,
  Theme,
  PublicUserConfig,
  VehicleConfig,
  StepType,
  ImageSection,
  MissingProperties,
} from './widget';

export const API_PATHS = {
  config: 'config',
  submission: 'submission',
  session: 'session',
  statistics: 'statistics',
  files: 'files',
} as const;

export type ApiPath = keyof typeof API_PATHS;
export type ApiError = {
  message: string;
};

export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

export type NotificationTime = 'anytime' | 'morning' | 'afternoon' | 'evening';
export type Fault = boolean | null;

export interface Integration {
  id: string;
  reference: string;
  name: string;
  status: string;
  key?: IntegrationKeys;
}
export type IntegrationKeys = 'brokerDirect' | 'axicloudLead';

export type Session = {
  createdAt: string;
  id: string;
  submissionId?: string;
  submissionReference?: string;
  userId: string;
  currentSession: Source | object;
  submission?: Partial<Submission>;
  statistics: {
    preSubmissionAccessCount: number;
    postSubmissionAccessCount: number;
  };
  userStatistics?: Statistics;
};

export type Submission = Source & {
  id: string;
  clientId?: string;
  clientInternalName?: string;
  meta: Record<string, unknown>;
  rejectedIntegrations?: string[];
  integrations?: Partial<Record<IntegrationKeys, Integration>>;
  camsReference?: string;
  createdAt: string;
};

export enum FormKey {
  Repair = 'repair-form',
  RetailerRepair = 'retailer-repair-form',
  repairReferralForm = 'repair-referral-form',
  SingleVehicleAccident = 'single-vehicle-form',
  SingleVehicleAccidentLight = 'single-vehicle-light-form',
  PatchSingleVehicleAccident = 'patch-single-vehicle-form',
  OptionalSingleVehicleAccident = 'optional-single-vehicle-form',
  OptionalSingleVehiclePersonalForm = 'optional-single-vehicle-personal-form',
  RepairFinderFormUs = 'repair-finder-form-us',
  RepairFinderForm = 'repair-finder-form',
  MultipleVehicleAccident = 'multiple-vehicle-form',
  AccidentReferral = 'accident-referral-form',
  BoschReferral = 'bosch-referral-form',
  JlrReferral = 'jlr-referral-form',
  JlrReferralV3 = 'jlr-referral-form-v3',
  RetailerReferral = 'retailer-referral-form',
}

export type Source = (RepairSubmission | AccidentSubmission | ReferralSubmission) & {
  missingProperties?: MissingProperties;
  context?: Record<string, string>;
};

export type AccidentSubmission = InsuredAccident | NonInsuredAccident;

export function isSubmission(sub: Submission | object): sub is Submission {
  return 'stepKey' in sub;
}
export function isSource(sub: Source | object): sub is Source {
  return (
    'missingProperties' in sub ||
    'context' in sub ||
    'date' in sub ||
    'location' in sub ||
    'user' in sub ||
    'vehicle' in sub ||
    'policyLicensePlate' in sub ||
    'agentDetails' in sub
  );
}
export interface InsuredAccident {
  // stepKey: FormKey.SingleVehicleAccident | FormKey.PatchSingleVehicleAccident | FormKey.MultipleVehicleAccident;
  stepKey: StepKey;
  stepType: StepType.CLAIMS;
  reference?: string;
  date: string;
  timezone: string;
  location: Address;
  vehicleLocation?: Address;
  vehicleRoadWorthy: boolean;
  driver: boolean[] | ThirdPartyDriver[] | boolean | ThirdPartyDriver;
  injured?: boolean;
  thirdParties?: Contact[] | boolean[] | boolean;
  witnesses?: Contact[] | boolean[] | boolean;
  reportedToPolice?: boolean;
  dashCamFootageAvailable?: boolean;
  notificationTime?: NotificationTime;
  fault?: Fault;
  insurance: InsuranceConfig;
  termsAccepted: boolean;
  user: Omit<PublicUserConfig, 'phone'> & { phone: Phone };
  vehicle: VehicleConfig;
  policyLicensePlate: string;
  vehicleImages?: ImageSection[];
  additionalImages?: ImageSection[];
  note?: string;
  testMode?: boolean;
  repairLocation?: RepairLocation;
  repairLocationLight?: RepairLocationLight;
}

export interface RepairLocation {
  postcode: string;
  note: string;
}

export interface NonInsuredAccident {
  stepKey: StepKey;
  // stepKey:
  //   | FormKey.SingleVehicleAccident
  //   | FormKey.PatchSingleVehicleAccident
  //   | FormKey.SingleVehicleAccident
  //   | FormKey.PatchSingleVehicleAccident;
  stepType: StepType.ASSISTANCE;
  reference?: string;
  date: string;
  timezone: string;
  location: Address;
  vehicleLocation?: Address;
  vehicleRoadWorthy: boolean;
  needRecovery: boolean;
  isRecentClaim: boolean;
  atRoadside: boolean;
  driver: boolean[] | Contact[] | boolean | Contact;
  injured?: boolean;
  thirdParties?: Contact[] | boolean[] | boolean;
  witnesses?: Contact[] | boolean[] | boolean;
  notificationTime?: { time: NotificationTime; note: string };
  fault?: Fault;
  termsAccepted: boolean;
  insurance?: null;
  user: Omit<PublicUserConfig, 'phone'> & { phone: Phone };
  vehicle: VehicleConfig;
  policyLicensePlate: string;
  vehicleImages?: ImageSection[];
  additionalImages?: ImageSection[];
  note?: string;
  testMode?: boolean;
  repairLocation?: RepairLocation;
  recoveryLocation?: Address;
  repairLocationLight: RepairLocationLight;
}

export interface RepairSubmission {
  // stepKey: FormKey.Repair;
  stepKey: StepKey;
  stepType: StepType.ASSISTANCE;
  repairLocation?: RepairLocation;
  reference?: string;
  vehicleLocation?: Address;
  vehicleRoadWorthy: boolean;
  notificationTime?: { time: NotificationTime; note: string };
  termsAccepted: boolean;
  user: Omit<PublicUserConfig, 'phone'> & { phone: Phone };
  vehicle: VehicleConfig;
  policyLicensePlate: string;
  vehicleImages?: ImageSection[];
  note?: string;
  testMode?: boolean;
  repairLocationLight?: RepairLocationLight;
  atRoadside: boolean;
}

export interface EmergencyServices {
  note?: string;
  services?: { const: string; title: string; checked?: boolean }[];
}

export type ReferralSubmission =
  | BaseReferralPayload
  | JlrReferralPayload
  | JlrReferralV2Payload
  | JlrReferralV3Payload
  | SuzukiReferralPayload;
export interface BaseReferralPayload {
  agentDetails: { user: PublicUserConfig };
  date: string;
  emergencyServices?: EmergencyServices;
  emergencyServicesDispatched?: boolean;
  location?: Address;
  note?: string;
  notificationTime?: NotificationTime;
  policyLicensePlate: string;
  reference?: string;
  // stepKey: 'accident-referral-form';
  stepKey: StepKey;
  stepType: StepType.REFERRAL;
  timezone: string;
  additionalDetails: boolean;
  user: PublicUserConfig;
  vehicle?: Partial<VehicleConfig>;
  context: {
    brand: string;
    referrer: string;
  };
}

export interface JlrReferralPayload {
  date: string;
  reportedToPolice?: boolean;
  policyLicensePlate: string;
  reference?: string;
  // stepKey: 'jlr-referral-form';
  stepKey: StepKey;
  stepType: StepType.REFERRAL;
  timezone: string;
  user: PublicUserConfig;
  vehicle?: Partial<VehicleConfig>;
  context: {
    brand: string;
    referrer: string;
  };
}

export interface JlrReferralV2Payload {
  date: string;
  reportedToPolice?: boolean;
  policyLicensePlate: string;
  reference?: string;
  // stepKey: 'jlr-referral-form';
  stepKey: StepKey;
  stepType: StepType.REFERRAL;
  timezone: string;
  user: PublicUserConfig;
  vehicle?: Partial<VehicleConfig>;
  context: {
    brand: string;
    referrer: string;
  };
  location?: Address;
  note?: string;
}

export interface SuzukiReferralPayload {
  date: string;
  reportedToPolice?: boolean;
  policyLicensePlate: string;
  reference?: string;
  stepKey: 'suzuki-referral-form';
  location?: Address;
  note?: string;
  stepType: StepType.REFERRAL;
  timezone: string;
  user: PublicUserConfig;
  vehicle?: Partial<VehicleConfig>;
  context: {
    brand: string;
    referrer: string;
  };
}

export interface JlrReferralV3Payload extends JlrReferralV2Payload {
  arcId: string;
}

export type ResponseTypeMap = {
  [API_PATHS.config]: Omit<ApiWidgetOptions, 'apiKey' | 'isConfigLoading' | 'error' | 'rootKey' | 'selectedClaim'> & {
    theme: Theme;
  };
  [API_PATHS.session]: Session;
  [API_PATHS.submission]: Submission;
  [API_PATHS.files]: unknown;
  [API_PATHS.statistics]: Session;
};
